import { createDarkTheme, type Theme, type BrandVariants } from '@fluentui/react-components';

export const ottoBrand: BrandVariants = {
  10: '#070a1f',
  20: '#0e1540',
  30: '#141f5e',
  40: '#1a297a',
  50: '#213496',
  60: '#2940b2',
  70: '#3350cc',
  80: '#5B6CF0',
  90: '#7585f5',
  100: '#8E9DF8',
  110: '#A8B4FA',
  120: '#C1CAFC',
  130: '#D5DDFD',
  140: '#E5EBFE',
  150: '#F0F3FF',
  160: '#F8F9FF',
};

const baseTheme = createDarkTheme(ottoBrand);

export const ottoDarkTheme: Theme = {
  ...baseTheme,

  // Backgrounds (blue-black, ~222° hue)
  colorNeutralBackground1: '#171B22',
  colorNeutralBackground1Hover: '#1E2229',
  colorNeutralBackground1Pressed: '#111419',
  colorNeutralBackground1Selected: '#1E2229',

  colorNeutralBackground2: '#0B0E14',
  colorNeutralBackground2Hover: '#111419',
  colorNeutralBackground2Pressed: '#0B0E14',
  colorNeutralBackground2Selected: '#111419',

  colorNeutralBackground3: '#111419',
  colorNeutralBackground3Hover: '#171B22',
  colorNeutralBackground3Pressed: '#0B0E14',
  colorNeutralBackground3Selected: '#171B22',

  colorNeutralBackground4: '#1E2229',
  colorNeutralBackground4Hover: '#262B35',
  colorNeutralBackground4Pressed: '#171B22',
  colorNeutralBackground4Selected: '#262B35',

  colorNeutralBackground5: '#262B35',
  colorNeutralBackground6: '#2d3148',

  // Subtle backgrounds
  colorSubtleBackground: 'transparent',
  colorSubtleBackgroundHover: '#1E2229',
  colorSubtleBackgroundPressed: '#171B22',
  colorSubtleBackgroundSelected: '#1E2229',
  colorSubtleBackgroundLightAlphaHover: 'rgba(255,255,255,0.04)',
  colorSubtleBackgroundLightAlphaPressed: 'rgba(255,255,255,0.02)',
  colorSubtleBackgroundLightAlphaSelected: 'rgba(255,255,255,0.04)',
  colorSubtleBackgroundInverted: 'transparent',
  colorSubtleBackgroundInvertedHover: 'rgba(0,0,0,0.1)',
  colorSubtleBackgroundInvertedPressed: 'rgba(0,0,0,0.2)',
  colorSubtleBackgroundInvertedSelected: 'rgba(0,0,0,0.1)',

  // Transparent backgrounds
  colorTransparentBackground: 'transparent',
  colorTransparentBackgroundHover: 'rgba(255,255,255,0.04)',
  colorTransparentBackgroundPressed: 'rgba(255,255,255,0.02)',
  colorTransparentBackgroundSelected: 'rgba(255,255,255,0.04)',

  // Foregrounds (cool gray, brightened to match Foundry contrast levels)
  colorNeutralForeground1: '#f0f2f8',
  colorNeutralForeground1Hover: '#f0f2f8',
  colorNeutralForeground1Pressed: '#f0f2f8',
  colorNeutralForeground1Selected: '#f0f2f8',

  colorNeutralForeground2: '#c8cdd8',
  colorNeutralForeground2Hover: '#f0f2f8',
  colorNeutralForeground2Pressed: '#f0f2f8',
  colorNeutralForeground2Selected: '#f0f2f8',
  colorNeutralForeground2BrandHover: '#8E9DF8',
  colorNeutralForeground2BrandPressed: '#7585f5',
  colorNeutralForeground2BrandSelected: '#8E9DF8',

  colorNeutralForeground3: '#a8b0c0',
  colorNeutralForeground3Hover: '#c8cdd8',
  colorNeutralForeground3Pressed: '#c8cdd8',
  colorNeutralForeground3Selected: '#c8cdd8',
  colorNeutralForeground3BrandHover: '#8E9DF8',
  colorNeutralForeground3BrandPressed: '#7585f5',
  colorNeutralForeground3BrandSelected: '#8E9DF8',

  colorNeutralForeground4: '#9298ac',
  colorNeutralForegroundDisabled: '#6b738f',

  // Strokes (light effects)
  colorNeutralStroke1: '#5e6694',
  colorNeutralStroke1Hover: '#5e6694',
  colorNeutralStroke1Pressed: '#3a4060',
  colorNeutralStroke1Selected: '#5B6CF0',

  colorNeutralStroke2: '#2d3148',
  colorNeutralStroke3: '#232740',

  colorNeutralStrokeSubtle: '#232740',
  colorNeutralStrokeAccessible: '#8890a6',
  colorNeutralStrokeAccessibleHover: '#c8cdd8',
  colorNeutralStrokeAccessiblePressed: '#8890a6',
  colorNeutralStrokeAccessibleSelected: '#5B6CF0',

  colorNeutralStrokeDisabled: '#232740',

  // Stencil / skeleton
  colorStrokeFocus1: '#0B0E14',
  colorStrokeFocus2: '#5B6CF0',

  colorNeutralStencil1: '#2d3148',
  colorNeutralStencil2: '#232740',
  colorNeutralStencil1Alpha: 'rgba(255,255,255,0.06)',
  colorNeutralStencil2Alpha: 'rgba(255,255,255,0.03)',

  // Elevated surface (tooltips, flyouts)
  colorNeutralBackgroundInverted: '#f0f2f8',
  colorNeutralForegroundInverted: '#0B0E14',
  colorNeutralForegroundInvertedLink: '#5B6CF0',

  // Scrollbar
  colorScrollbarOverlay: 'rgba(255,255,255,0.12)',

  // Shadows (deeper, cooler)
  shadow2: '0 1px 2px rgba(0,0,0,0.6)',
  shadow4: '0 2px 4px rgba(0,0,0,0.5)',
  shadow8: '0 4px 8px rgba(0,0,0,0.5)',
  shadow16: '0 8px 16px rgba(0,0,0,0.5)',
  shadow28: '0 14px 28px rgba(0,0,0,0.5)',
  shadow64: '0 32px 64px rgba(0,0,0,0.6)',

  shadow2Brand: '0 1px 2px rgba(91,108,240,0.3)',
  shadow4Brand: '0 2px 4px rgba(91,108,240,0.25)',
  shadow8Brand: '0 4px 8px rgba(91,108,240,0.25)',
  shadow16Brand: '0 8px 16px rgba(91,108,240,0.25)',
  shadow28Brand: '0 14px 28px rgba(91,108,240,0.25)',
  shadow64Brand: '0 32px 64px rgba(91,108,240,0.3)',
};
